import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Facebook as FacebookIcon } from "@mui/icons-material";
import { Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useConfetti } from "../../context/ConfettiContext";
import AudioService, { AUDIO_EFFECT } from "../../services/AudioService";
import Logger from "../../services/Logger";
import { SURFACE } from "../../dark-theme/palette";
import { browseViewButtonSx } from "../browse-jigsaw/JigsawBrowseStoryCard";
import { usePagesTranslation } from "../locales";
import LetterWheel, {
    buildWheelTiles,
} from "../../prototype/mobile-word-wheel/components/LetterWheel";
import {
    DEMO_GUESS_POST,
    fetchLatestPostedWordWheelSocial,
    gradientCssForWord,
    maskWordSlots,
    } from "../landing/wordWheelSocialApi";

const SOLVED_PREFIX = "pi.dashboard.fbWord.";

function normalizeWord(raw) {
    return String(raw || "")
        .toUpperCase()
        .replace(/[^A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸÇÑ]/g, "");
}

function parseWheelLetters(raw, word) {
    const fromWord = () => normalizeWord(word).split("").filter(Boolean);
    if (Array.isArray(raw)) {
        const letters = raw.map((x) => normalizeWord(x)).filter(Boolean);
        return letters.length ? letters : fromWord();
    }
    const s = String(raw || "").trim().toUpperCase();
    if (!s) return fromWord();
    if (s.includes(",")) {
        const parts = s.split(",").map((p) => normalizeWord(p)).filter(Boolean);
        return parts.length ? parts : fromWord();
    }
    const compact = normalizeWord(s);
    return compact ? compact.split("") : fromWord();
}

function solvedKey(word) {
    return `${SOLVED_PREFIX}${word}`;
}

function readSolved(word) {
    if (!word || typeof localStorage === "undefined") return false;
    try {
        return localStorage.getItem(solvedKey(word)) === "1";
    } catch {
        return false;
    }
}

function writeSolved(word) {
    if (!word || typeof localStorage === "undefined") return;
    try {
        localStorage.setItem(solvedKey(word), "1");
    } catch {
        /* ignore quota */
    }
}

export default function DashboardWordWheelWidget() {
    const { t } = usePagesTranslation();
    const { triggerConfetti } = useConfetti();
    const [loading, setLoading] = useState(true);
    const [post, setPost] = useState(null);
    const [solved, setSolved] = useState(false);
    const [justFound, setJustFound] = useState(false);
    const [wrong, setWrong] = useState(false);
    const [copied, setCopied] = useState(false);
    const [selectedIndices, setSelectedIndices] = useState([]);
    const selectedRef = useRef([]);

    const word = useMemo(() => normalizeWord(post?.word), [post]);
    const revealed = useMemo(
        () => (word ? maskWordSlots(word, post?.maskedWord) : []),
        [word, post],
    );
    const slots = solved ? word.split("") : revealed;
    const cardGradient = useMemo(() => gradientCssForWord(word), [word]);
    const meaning = String(post?.meaning || "").trim();
    const facebookUrl = String(post?.facebookUrl || "").trim();
    const facebookLink = facebookUrl || String(process.env.REACT_APP_FACEBOOK_URL || "").trim();
    const wheelTiles = useMemo(
        () => buildWheelTiles(parseWheelLetters(post?.wheelLetters, word), post?.id || word || "fb-word"),
        [post, word],
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await fetchLatestPostedWordWheelSocial();
                if (cancelled) return;
                const nextWord = normalizeWord(data?.word);
                if (nextWord) {
                    setPost({
                        id: data.id,
                        word: nextWord,
                        meaning: String(data.meaning || "").trim(),
                        maskedWord: data.maskedWord || "",
                        wheelLetters: data.wheelLetters || "",
                        facebookUrl: String(data.facebookUrl || "").trim(),
                        facebookPostId: data.facebookPostId || "",
                    });
                    setSolved(readSolved(nextWord));
                } else {
                    setPost(DEMO_GUESS_POST);
                    setSolved(readSolved(normalizeWord(DEMO_GUESS_POST.word)));
                }
            } catch (e) {
                Logger.warn("Dashboard Facebook word load failed:", e);
                if (!cancelled) {
                    setPost(DEMO_GUESS_POST);
                    setSolved(readSolved(normalizeWord(DEMO_GUESS_POST.word)));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const markSolved = useCallback(() => {
        setSolved(true);
        setJustFound(true);
        setWrong(false);
        setSelectedIndices([]);
        selectedRef.current = [];
        writeSolved(word);
        triggerConfetti(5000);
        void AudioService.play(AUDIO_EFFECT.WORD_RESOLVED);
    }, [word, triggerConfetti]);

    const setSelection = useCallback((updater) => {
        setSelectedIndices((prev) => {
            const current = Array.isArray(prev) ? prev : [];
            const next = typeof updater === "function" ? updater(current) : updater;
            const list = Array.isArray(next) ? next : [];
            selectedRef.current = list;
            return list;
        });
    }, []);

    const commitSelection = useCallback(() => {
        const indices = Array.isArray(selectedRef.current) ? selectedRef.current : [];
        const spelled = normalizeWord(
            indices.map((index) => wheelTiles[index]?.letter || "").join(""),
        );
        setSelectedIndices([]);
        selectedRef.current = [];
        if (solved || !spelled) return;
        if (spelled === word) {
            markSolved();
            return;
        }
        if (spelled.length === word.length) {
            setWrong(true);
        }
    }, [wheelTiles, solved, word, markSolved]);

    const handleReply = async () => {
        if (!facebookLink) return;
        try {
            await navigator.clipboard.writeText(word);
            setCopied(true);
        } catch (e) {
            Logger.warn("Facebook reply copy failed:", e);
            setCopied(false);
        }
        window.open(facebookLink, "_blank", "noopener,noreferrer");
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                height: "100%",
                width: "100%",
                minWidth: 0,
                flex: 1,
                overflow: justFound ? "visible" : "hidden",
                display: "flex",
                flexDirection: "column",
                bgcolor: SURFACE.card,
                borderColor: SURFACE.border,
                borderRadius: 2,
            }}
        >
            <Stack spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="overline" sx={{ letterSpacing: 1.2, color: "text.secondary", fontWeight: 700 }}>
                    {t("dashboard.today-word-wheel")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                        component={RouterLink}
                        to="/puzzles/word-wheel/daily"
                        variant="contained"
                        sx={{ ...browseViewButtonSx, minHeight: 44 }}
                    >
                        {t("landing.play-now")}
                    </Button>
                    <Button
                        component={RouterLink}
                        to="/puzzles/word-wheel/journey"
                        variant="contained"
                        sx={{ ...browseViewButtonSx, minHeight: 44 }}
                    >
                        {t("landingApp.ww-journey-cta", "Go to Journey")}
                    </Button>
                </Stack>
            </Stack>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : !word ? (
                <Typography variant="body2" color="text.secondary">
                    {t("dashboard.wheel-unavailable")}
                </Typography>
            ) : (
                <>
                    <Box
                        sx={{
                            mt: 0.5,
                            mb: 1.5,
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: 2,
                            background: cardGradient,
                            color: "#fff",
                            textAlign: "center",
                            minWidth: 0,
                            "@keyframes fbWordGlow": {
                                "0%, 100%": { boxShadow: "0 0 0 0 rgba(94,234,212,0)" },
                                "40%": { boxShadow: "0 0 32px 8px rgba(250,204,21,0.65)" },
                                "70%": { boxShadow: "0 0 24px 6px rgba(94,234,212,0.55)" },
                            },
                            animation: justFound ? "fbWordGlow 1.6s ease-in-out 2" : "none",
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 800, mb: 1.5, textShadow: "0 1px 8px rgba(0,0,0,0.35)" }}>
                            {t("landingApp.ww-fb-prompt", "Got a second? What's this word?")}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "center",
                                gap: 0.75,
                                mb: meaning ? 1.25 : 0,
                            }}
                            aria-label={t("dashboard.wheel-type-hint")}
                        >
                            {word.split("").map((letter, i) => {
                                const shown = slots[i] || "";
                                return (
                                    <Box
                                        key={`${letter}-${i}`}
                                        component="span"
                                        sx={{
                                            width: { xs: 36, sm: 44 },
                                            height: { xs: 48, sm: 56 },
                                            minWidth: 36,
                                            minHeight: 44,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: { xs: "1.35rem", sm: "1.6rem" },
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            borderRadius: "10px",
                                            boxSizing: "border-box",
                                            border: "3px solid",
                                            borderColor: shown ? (solved ? "#facc15" : "#5eead4") : "rgba(255,255,255,0.55)",
                                            bgcolor: shown ? "rgba(255,255,255,0.95)" : "transparent",
                                            color: shown ? "#0f172a" : "#fff",
                                            "@keyframes fbLetterPop": {
                                                "0%": { transform: "scale(0.55)", opacity: 0.4 },
                                                "55%": { transform: "scale(1.18)" },
                                                "100%": { transform: "scale(1)", opacity: 1 },
                                            },
                                            animation: justFound ? `fbLetterPop 0.45s ease-out ${i * 0.06}s both` : "none",
                                        }}
                                    >
                                        {shown || "_"}
                                    </Box>
                                );
                            })}
                        </Box>
                        {meaning ? (
                            <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.96, textShadow: "0 1px 8px rgba(0,0,0,0.3)" }}>
                                {meaning}
                            </Typography>
                        ) : null}
                    </Box>
                    {!solved && wheelTiles.length > 0 ? (
                        <Box sx={{ width: "100%", maxWidth: 220, height: 200, mx: "auto", mb: 1, flexShrink: 0 }}>
                            <LetterWheel
                                tiles={wheelTiles}
                                selectedIndices={Array.isArray(selectedIndices) ? selectedIndices : []}
                                onSelectionChange={(updater) => {
                                    setWrong(false);
                                    setSelection(updater);
                                }}
                                onDragEnd={commitSelection}
                                backdrop="gradient"
                                edgeInset={1}
                                fill
                            />
                        </Box>
                    ) : null}
                    {solved ? (
                        <Typography variant="body2" color="success.light" sx={{ fontWeight: 700, mb: 1 }}>
                            {t("dashboard.wheel-resolved")}
                        </Typography>
                    ) : wrong ? (
                        <Typography variant="body2" color="warning.light" sx={{ fontWeight: 700, mb: 1 }}>
                            {t("dashboard.fb-word-wrong")}
                        </Typography>
                    ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                            {t("dashboard.wheel-type-hint")}
                        </Typography>
                    )}
                    {solved && facebookLink ? (
                        <Stack spacing={1} sx={{ mt: "auto" }}>
                            <Button
                                variant="contained"
                                startIcon={<FacebookIcon />}
                                onClick={handleReply}
                                sx={{
                                    textTransform: "none",
                                    alignSelf: "stretch",
                                    minHeight: 48,
                                    fontWeight: 800,
                                    fontSize: "1rem",
                                    bgcolor: "#1877F2",
                                    "&:hover": { bgcolor: "#166fe5" },
                                    "@keyframes fbCtaPulse": {
                                        "0%, 100%": {
                                            transform: "scale(1)",
                                            boxShadow: "0 0 0 0 rgba(24,119,242,0.55)",
                                        },
                                        "50%": {
                                            transform: "scale(1.03)",
                                            boxShadow: "0 0 0 12px rgba(24,119,242,0)",
                                        },
                                    },
                                    animation: justFound && !copied ? "fbCtaPulse 1.4s ease-out infinite" : "none",
                                }}
                            >
                                {t("dashboard.fb-post-word")}
                            </Button>
                            {copied ? (
                                <Typography variant="caption" color="text.secondary">
                                    {t("dashboard.fb-word-copied")}
                                </Typography>
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    {t("dashboard.fb-word-reply-hint")}
                                </Typography>
                            )}
                        </Stack>
                    ) : null}
                </>
            )}
        </Paper>
    );
}
